import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import classnames from 'classnames';

import AdditionDetailsModalElements from '../ElementComponents/AdditionDetailsModalElements';
import { generateFormSchema, defaultFieldValues } from './constants';
import {
  typeModalMapping,
  MODAL_DEFAULT,
  extraModalMapping,
} from './modalConfig';
import { useProcessExpenseClaimCtx } from '../../../pages/ExpenseClaims/HR/context/ProcessExpenseClaimProvider';
import { CLAIM_FORM_TYPES } from '../../../pages/ExpenseClaims/Employee/EmployeeExpenseClaim/constants';

import './ProcessClaimAdditionalDetailsModal.scss';

export default function ProcessClaimAdditionalDetailsModal({
  isOpen,
  toggle,
  modelType,
  rowIndex,
  rowData,
  formType,
  expenseClaimHeaders,
  checkLastRowAndAdd = null,
}) {
  // main form context
  const { formMethods: mainFormMethods } = useProcessExpenseClaimCtx();

  // config for this modal type
  const config = useMemo(() => typeModalMapping[modelType] || {}, [modelType]);

  const validationSchema = generateFormSchema(modelType);

  // react-hook-form setup
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultFieldValues,
    mode: 'onChange',
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = methods;

  // prepare extra‐tab config safely
  const extraConfig = extraModalMapping[modelType] || {};
  const hasExtra = Object.keys(extraConfig).length > 0;
  const [activeTab, setActiveTab] = useState('1');

  // submit handler
  const formSubmit = async data => {
    Object.entries(data).forEach(([key, val]) => {
      mainFormMethods.setValue(`claims.${rowIndex}.${key}`, val, {
        shouldValidate: true,
      });
    });
    await mainFormMethods.trigger(`claims.${rowIndex}`);
    checkLastRowAndAdd();
    toggle();
  };

  // reset form when opening / rowData changes
  useEffect(() => {
    if (!isOpen) {
      reset(defaultFieldValues);
      return;
    }
    if (rowData) {
      const initialValues = Object.entries(MODAL_DEFAULT).reduce(
        (acc, [fieldName, cfg]) => {
          acc[fieldName] = rowData[cfg.fieldName] ?? '';
          return acc;
        },
        {},
      );
      reset(initialValues);
    }
  }, [rowData, config, reset, isOpen]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle} className="border-bottom py-3">
        Additional Details
      </ModalHeader>

      <FormProvider {...methods}>
        <ModalBody style={{ maxHeight: '55vh', overflowY: 'auto' }}>
          {/* Tabs (only if there is an extra form) */}
          {hasExtra && (
            <Nav tabs className="mb-3">
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTab === '1',
                    'cursor-pointer': true,
                  })}
                  onClick={() => setActiveTab('1')}
                >
                  Additional
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTab === '2',
                    'cursor-pointer': true,
                  })}
                  onClick={() => setActiveTab('2')}
                >
                  {modelType === 'E103' ? 'School Info' : 'More Details'}
                </NavLink>
              </NavItem>
            </Nav>
          )}

          <TabContent activeTab={activeTab}>
            {/* Tab 1: default additional fields */}
            <TabPane tabId="1">
              {Object.entries(MODAL_DEFAULT || {}).map(
                ([fieldName, fieldConfig]) => {
                  const cfg = config[fieldName] || fieldConfig;
                  if (fieldConfig?.notShow) return null;
                  return (
                    <div key={fieldName} className="mb-3">
                      <AdditionDetailsModalElements
                        formMethods={methods}
                        name={cfg.fieldName}
                        type={cfg.type}
                        config={cfg}
                        rowData={rowData}
                        formType={formType}
                        expenseClaimHeaders={expenseClaimHeaders}
                      />
                    </div>
                  );
                },
              )}
            </TabPane>

            {/* Tab 2: extra fields */}
            {hasExtra && (
              <TabPane tabId="2">
                {Object.entries(extraConfig).map(([fieldName, fieldConfig]) => (
                  <div key={fieldName} className="mb-3">
                    <AdditionDetailsModalElements
                      formMethods={methods}
                      name={fieldConfig.fieldName}
                      type={fieldConfig.type}
                      config={fieldConfig}
                      rowData={rowData}
                      formType={formType}
                      expenseClaimHeaders={expenseClaimHeaders}
                    />
                  </div>
                ))}
              </TabPane>
            )}
          </TabContent>
        </ModalBody>

        <ModalFooter className="border-top pt-1">
          <div className="d-flex gap-2 w-100 pt-2">
            <Button onClick={toggle} className="additional-modal-cancel-button">
              Cancel
            </Button>
            {formType !== CLAIM_FORM_TYPES.VIEW && (
              <Button
                type="button"
                className="additional-modal-save-button"
                onClick={handleSubmit(formSubmit)}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            )}
          </div>
        </ModalFooter>
      </FormProvider>
    </Modal>
  );
}
